import { memo, useState } from 'react';
import { BsCircleFill } from 'react-icons/bs';
import ImageWithFallback from '../../common/ImageWithFallback';
import Carousel from '../../common/Carousel';
import { Hotel } from '../../../core/isValidHotel';
import Button from '../../common/Button/Button';
import analytics from '../../../core/analytics/analytics';
import Images from '../../common/Icons/Images';
import SkipTranslationText from '../../common/SkipTranslationText';

type Props = {
  hotel?: Hotel;
  setIsModalOpen: (bool: boolean) => void;
};

const HotelTopBanner = ({ hotel, setIsModalOpen }: Props) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  const mainPhoto = hotel?.photos?.selectedPhotos.find((photo) => photo.selection === 'MAIN');
  const allPhotos = hotel?.photos?.allPhotos || [];
  const topImages = allPhotos.filter((photo) => photo.selection !== 'MAIN').slice(0, 4);

  const photos = mainPhoto ? [mainPhoto, ...topImages].filter(Boolean) : allPhotos.slice(0, 5);

  const customPaging = (i: number) => (
    <BsCircleFill
      key={i}
      size={i === currentSlideIndex ? 12 : 8}
      className={`text-micro text-white ${
        i === currentSlideIndex ? 'opacity-100' : 'opacity-50'
      } cursor-pointer`}
    />
  );

  const appendDots = (dots: JSX.Element) => (
    <div className="w-full">
      <ul className="flex items-center justify-center gap-3">{dots}</ul>
    </div>
  );

  const maxSlidesToShow = 1;

  const settings = {
    className: 'grid',
    dots: photos.length > maxSlidesToShow,
    autoplay: false,
    draggable: true,
    arrows: false,
    dotsClass: 'absolute bottom-7 w-full px-2',
    beforeChange: (_: number, next: number) => setCurrentSlideIndex(next),
    customPaging,
    appendDots,
    maxSlidesToShow,
  };

  const event = {
    onClickSeeMorePhotos: () => {
      setIsModalOpen(true);
      analytics.google.ecommerce.track.viewGallery(hotel?.id);
    },
  };

  return (
    <>
      <div className="hidden h-100 justify-center space-x-2 sm:flex lg:h-[480px]">
        <div
          className="relative size-full"
          onClick={event.onClickSeeMorePhotos}
          onKeyDown={event.onClickSeeMorePhotos}
          role="button"
          tabIndex={0}
        >
          <ImageWithFallback
            alt={photos[0]?.alt || 'main-photo'}
            src={photos[0]?.url || '/fallback-img-1440x440.png'}
            layout="fill"
            objectFit="cover"
            fallbackSrc="/fallback-img-1440x440.png"
            className="relative lg:rounded-l-lg"
            priority
            sizes="(max-width: 768px) 100vw, 640px"
            quality={50}
          />
        </div>
        <div className="relative flex h-full w-1/2 flex-col space-y-2">
          <div
            className="relative w-full grow"
            onClick={event.onClickSeeMorePhotos}
            onKeyDown={event.onClickSeeMorePhotos}
            role="button"
            tabIndex={0}
          >
            <ImageWithFallback
              alt={photos[1]?.alt || 'top-image-1'}
              src={photos[1]?.url || '/fallback-img-1440x440.png'}
              layout="fill"
              objectFit="cover"
              fallbackSrc="/fallback-img-1440x440.png"
              className="relative"
              priority
              sizes="320px"
              quality={50}
            />
          </div>
          <div
            className="relative w-full grow"
            onClick={event.onClickSeeMorePhotos}
            onKeyDown={event.onClickSeeMorePhotos}
            role="button"
            tabIndex={0}
          >
            <ImageWithFallback
              alt={photos[2]?.alt || 'top-image-2'}
              src={photos[2]?.url || '/fallback-img-1440x440.png'}
              layout="fill"
              objectFit="cover"
              fallbackSrc="/fallback-img-1440x440.png"
              className="relative"
              priority
              sizes="320px"
              quality={50}
            />
          </div>
        </div>
        <div className="relative flex h-full w-1/2 flex-col space-y-2">
          <div
            className="relative w-full grow"
            onClick={event.onClickSeeMorePhotos}
            onKeyDown={event.onClickSeeMorePhotos}
            role="button"
            tabIndex={0}
          >
            <ImageWithFallback
              alt={photos[3]?.alt || 'top-image-3'}
              src={photos[3]?.url || '/fallback-img-1440x440.png'}
              layout="fill"
              objectFit="cover"
              fallbackSrc="/fallback-img-1440x440.png"
              className="relative lg:rounded-tr-lg"
              priority
              sizes="320px"
              quality={50}
            />
          </div>
          <div
            className="relative w-full grow"
            onClick={event.onClickSeeMorePhotos}
            onKeyDown={event.onClickSeeMorePhotos}
            role="button"
            tabIndex={0}
          >
            <ImageWithFallback
              alt={photos[4]?.alt || 'top-image-4'}
              src={photos[4]?.url || '/fallback-img-1440x440.png'}
              layout="fill"
              objectFit="cover"
              fallbackSrc="/fallback-img-1440x440.png"
              className="relative lg:rounded-br-lg"
              priority
              sizes="320px"
              quality={50}
            />
            <div className="absolute size-full bg-black/40 lg:rounded-br-lg">
              <div className="flex h-full flex-col justify-center whitespace-normal text-center text-2xl font-bold text-white">
                <span>
                  <SkipTranslationText
                    i18nKey="hotel:hotel_general_info.see_more_photos"
                    values={{
                      count: allPhotos.length,
                    }}
                  />
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* mobile view */}
      <div
        className="relative block sm:hidden"
        onClick={event.onClickSeeMorePhotos}
        onKeyDown={event.onClickSeeMorePhotos}
        role="button"
        tabIndex={0}
      >
        <Carousel
          settings={settings}
          items={photos}
          renderItem={(photo) => {
            return (
              <div key={photo?.url} className="relative min-h-[250px] w-full">
                <ImageWithFallback
                  alt={photo?.alt || 'main-photo'}
                  src={photo?.url || '/fallback-img-1440x440.png'}
                  layout="fill"
                  objectFit="cover"
                  fallbackSrc="/fallback-img-1440x440.png"
                />
              </div>
            );
          }}
        />
        <Button
          className="absolute bottom-4 right-2 block rounded-lg bg-black/60 p-2 text-regular font-bold"
          textColor="white"
          size="small"
        >
          <div className="flex items-center">
            <Images size={24} color="white" />
            <span className="ml-2">+{allPhotos.length}</span>
          </div>
        </Button>
      </div>
    </>
  );
};

export default memo(HotelTopBanner);
